/**
 * Cost Tracker for LLM API Usage - Sprint 11g Dynamic Pricing
 *
 * TypeScript port of Python cost_tracker.py (lines 1-230)
 *
 * Tracks token usage and calculates costs across different LLM providers.
 *
 * SPRINT 11g: Dynamic Pricing via ConfigService
 *
 * Pricing lookup order:
 * 1. ConfigService (getPricingSync) - Dynamic pricing from OpenRouter
 * 2. Static PRICING fallback - Bundled defaults for offline/unknown models
 *
 * @example
 * ```typescript
 * const tracker = new CostTracker();
 *
 * // Sync tracking (uses ConfigService cache)
 * const cost = tracker.trackCall('anthropic', 'claude-3-5-haiku-20241022', 1000, 500);
 *
 * // Async tracking (fetches fresh pricing if needed)
 * const cost = await tracker.trackCallAsync('anthropic', 'claude-3-5-haiku-20241022', 1000, 500);
 * ```
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 * @see docs/sprints/ownyou-sprint11g-spec.md
 */
import { configService } from '@ownyou/llm-client';
/**
 * Create LLMCall with default timestamp
 *
 * Python equivalent: LLMCall(...) with default_factory for timestamp
 *
 * @param provider - LLM provider name
 * @param model - Model name
 * @param prompt_tokens - Input tokens
 * @param completion_tokens - Output tokens
 * @param cost_usd - Cost in USD
 * @returns LLMCall object
 */
function createLLMCall(provider, model, prompt_tokens, completion_tokens, cost_usd) {
    return {
        provider,
        model,
        prompt_tokens,
        completion_tokens,
        cost_usd,
        // Python line 20: datetime.now().isoformat() + "Z"
        timestamp: new Date().toISOString(),
    };
}
/**
 * Create ProviderStats with default values
 *
 * Python equivalent: ProviderStats(provider=provider) with field defaults
 *
 * @param provider - Provider name
 * @returns ProviderStats object with zero-initialized counters
 */
function createProviderStats(provider) {
    return {
        // Python line 26: provider: str
        provider,
        // Python line 27: calls: int = 0
        calls: 0,
        // Python line 28: prompt_tokens: int = 0
        prompt_tokens: 0,
        // Python line 29: completion_tokens: int = 0
        completion_tokens: 0,
        // Python line 30: total_cost_usd: float = 0.0
        total_cost_usd: 0.0,
    };
}
// ============================================================================
// COST TRACKER CLASS
// ============================================================================
/**
 * Track LLM API costs across multiple providers
 *
 * Python source: cost_tracker.py:33-230 (class CostTracker)
 *
 * Pricing data comes ONLY from llm-prices.com via ConfigService.
 * No static fallbacks - if llm-prices.com doesn't have pricing, cost is 0.
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
export class CostTracker {
    // Python line 84: self.calls: list[LLMCall] = []
    calls;
    // Python line 85: self.provider_stats: Dict[str, ProviderStats] = {}
    provider_stats;
    // Python line 86: self.session_start = datetime.now().isoformat() + "Z"
    session_start;
    /**
     * Initialize cost tracker
     *
     * Python source: cost_tracker.py:82-86 (def __init__)
     */
    constructor() {
        // Python line 84: self.calls: list[LLMCall] = []
        this.calls = [];
        // Python line 85: self.provider_stats: Dict[str, ProviderStats] = {}
        this.provider_stats = {};
        // Python line 86: self.session_start = datetime.now().isoformat() + "Z"
        this.session_start = new Date().toISOString();
    }
    /**
     * Track a single LLM API call (object params interface)
     *
     * This method wraps trackCall() to provide compatibility with
     * the AnalyzerLLMClient.CostTracker interface which uses object params.
     *
     * @param params - Object with provider, model, prompt_tokens, completion_tokens
     * @returns Cost in USD for this call
     */
    track_call(params) {
        return this.trackCall(params.provider, params.model, params.prompt_tokens, params.completion_tokens);
    }
    /**
     * Track a single LLM API call and calculate cost
     *
     * Pricing comes ONLY from llm-prices.com via ConfigService.
     * If pricing is not available, cost will be 0.
     *
     * @param provider - LLM provider name (openai, claude, ollama, groq, etc.)
     * @param model - Model name (gpt-4o-mini, claude-sonnet-4, etc.)
     * @param prompt_tokens - Number of input tokens
     * @param completion_tokens - Number of output tokens
     * @returns Cost in USD for this call
     */
    trackCall(provider, model, prompt_tokens, completion_tokens) {
        let totalCost = 0.0;
        // Get pricing from ConfigService (which uses llm-prices.com)
        const configPricing = configService.getPricingSync(model);
        if (configPricing && (configPricing.inputPer1M > 0 || configPricing.outputPer1M > 0)) {
            const inputCost = (prompt_tokens / 1_000_000) * configPricing.inputPer1M;
            const outputCost = (completion_tokens / 1_000_000) * configPricing.outputPer1M;
            totalCost = inputCost + outputCost;
        }
        // Python lines 136-142: Record call
        // call = LLMCall(
        //     provider=provider,
        //     model=model,
        //     prompt_tokens=prompt_tokens,
        //     completion_tokens=completion_tokens,
        //     cost_usd=total_cost
        // )
        const call = createLLMCall(provider, model, prompt_tokens, completion_tokens, totalCost);
        // Python line 143: self.calls.append(call)
        this.calls.push(call);
        // Python lines 146-147: Update provider stats
        // if provider not in self.provider_stats:
        if (!(provider in this.provider_stats)) {
            // self.provider_stats[provider] = ProviderStats(provider=provider)
            this.provider_stats[provider] = createProviderStats(provider);
        }
        // Python line 149: stats = self.provider_stats[provider]
        const stats = this.provider_stats[provider];
        // Python line 150: stats.calls += 1
        stats.calls += 1;
        // Python line 151: stats.prompt_tokens += prompt_tokens
        stats.prompt_tokens += prompt_tokens;
        // Python line 152: stats.completion_tokens += completion_tokens
        stats.completion_tokens += completion_tokens;
        // Python line 153: stats.total_cost_usd += total_cost
        stats.total_cost_usd += totalCost;
        // Python line 155: return total_cost
        return totalCost;
    }
    /**
     * Get total cost across all providers
     *
     * Python source: cost_tracker.py:157-159 (def get_total_cost)
     *
     * @returns Total cost in USD
     */
    getTotalCost() {
        // Python line 159: return sum(stats.total_cost_usd for stats in self.provider_stats.values())
        return Object.values(this.provider_stats).reduce((sum, stats) => sum + stats.total_cost_usd, 0);
    }
    /**
     * Get total tokens (input + output) across all providers
     *
     * Python source: cost_tracker.py:161-166 (def get_total_tokens)
     *
     * @returns Total token count
     */
    getTotalTokens() {
        // Python lines 163-166:
        // return sum(
        //     stats.prompt_tokens + stats.completion_tokens
        //     for stats in self.provider_stats.values()
        // )
        return Object.values(this.provider_stats).reduce((sum, stats) => sum + stats.prompt_tokens + stats.completion_tokens, 0);
    }
    /**
     * Get formatted cost summary
     *
     * Python source: cost_tracker.py:168-201 (def get_summary)
     *
     * @param emails_processed - Number of emails processed (for per-email cost)
     * @returns Formatted summary string
     */
    getSummary(emails_processed) {
        // Python line 178: total_cost = self.get_total_cost()
        const totalCost = this.getTotalCost();
        // Python line 179: total_tokens = self.get_total_tokens()
        const totalTokens = this.getTotalTokens();
        // Python line 180: total_calls = len(self.calls)
        const totalCalls = this.calls.length;
        // Python line 182: lines = []
        const lines = [];
        // Python line 183: lines.append("=== LLM COST SUMMARY ===")
        lines.push('=== LLM COST SUMMARY ===');
        // Python line 184: lines.append(f"Total Calls: {total_calls}")
        lines.push(`Total Calls: ${totalCalls}`);
        // Python line 185: lines.append(f"Total Tokens: {total_tokens:,}")
        lines.push(`Total Tokens: ${totalTokens.toLocaleString()}`);
        // Python line 186: lines.append(f"Total Cost: ${total_cost:.4f} USD")
        lines.push(`Total Cost: $${totalCost.toFixed(4)} USD`);
        // Python line 188: if emails_processed and emails_processed > 0:
        if (emails_processed && emails_processed > 0) {
            // Python line 189: cost_per_email = total_cost / emails_processed
            const costPerEmail = totalCost / emails_processed;
            // Python line 190: lines.append(f"Cost per Email: ${cost_per_email:.4f} USD")
            lines.push(`Cost per Email: $${costPerEmail.toFixed(4)} USD`);
        }
        // Python line 192: if len(self.provider_stats) > 1:
        if (Object.keys(this.provider_stats).length > 1) {
            // Python lines 193-194: lines.append("")
            lines.push('');
            // lines.append("=== BY PROVIDER ===")
            lines.push('=== BY PROVIDER ===');
            // Python line 195: for provider, stats in sorted(self.provider_stats.items()):
            for (const [provider, stats] of Object.entries(this.provider_stats).sort()) {
                // Python line 196: lines.append(f"{provider}:")
                lines.push(`${provider}:`);
                // Python line 197: lines.append(f"  Calls: {stats.calls}")
                lines.push(`  Calls: ${stats.calls}`);
                // Python line 198: lines.append(f"  Tokens: {stats.prompt_tokens + stats.completion_tokens:,}")
                lines.push(`  Tokens: ${(stats.prompt_tokens + stats.completion_tokens).toLocaleString()}`);
                // Python line 199: lines.append(f"  Cost: ${stats.total_cost_usd:.4f} USD")
                lines.push(`  Cost: $${stats.total_cost_usd.toFixed(4)} USD`);
            }
        }
        // Python line 201: return "\n".join(lines)
        return lines.join('\n');
    }
    /**
     * Get statistics as dictionary (for JSON export)
     *
     * Python source: cost_tracker.py:203-230 (def get_stats_dict)
     *
     * @returns Statistics object
     */
    getStatsDict() {
        // Python lines 205-230: return { ... }
        return {
            // Python line 206: "session_start": self.session_start,
            session_start: this.session_start,
            // Python line 207: "total_calls": len(self.calls),
            total_calls: this.calls.length,
            // Python line 208: "total_tokens": self.get_total_tokens(),
            total_tokens: this.getTotalTokens(),
            // Python line 209: "total_cost_usd": self.get_total_cost(),
            total_cost_usd: this.getTotalCost(),
            // Python lines 210-218: "providers": { ... }
            providers: Object.fromEntries(Object.entries(this.provider_stats).map(([provider, stats]) => [
                provider,
                {
                    // Python line 212: "calls": stats.calls,
                    calls: stats.calls,
                    // Python line 213: "prompt_tokens": stats.prompt_tokens,
                    prompt_tokens: stats.prompt_tokens,
                    // Python line 214: "completion_tokens": stats.completion_tokens,
                    completion_tokens: stats.completion_tokens,
                    // Python line 215: "total_cost_usd": stats.total_cost_usd
                    total_cost_usd: stats.total_cost_usd,
                },
            ])),
            // Python lines 219-229: "calls": [ ... ]
            calls: this.calls.map((call) => ({
                // Python line 221: "provider": call.provider,
                provider: call.provider,
                // Python line 222: "model": call.model,
                model: call.model,
                // Python line 223: "prompt_tokens": call.prompt_tokens,
                prompt_tokens: call.prompt_tokens,
                // Python line 224: "completion_tokens": call.completion_tokens,
                completion_tokens: call.completion_tokens,
                // Python line 225: "cost_usd": call.cost_usd,
                cost_usd: call.cost_usd,
                // Python line 226: "timestamp": call.timestamp
                timestamp: call.timestamp,
            })),
        };
    }
    /**
     * Track a single LLM API call with async pricing lookup
     *
     * Pricing comes ONLY from llm-prices.com via ConfigService.
     * If pricing is not available, cost will be 0.
     *
     * @param provider - LLM provider name
     * @param model - Model name
     * @param prompt_tokens - Number of input tokens
     * @param completion_tokens - Number of output tokens
     * @returns Promise<number> - Cost in USD for this call
     */
    async trackCallAsync(provider, model, prompt_tokens, completion_tokens) {
        let totalCost = 0.0;
        // Get pricing from ConfigService (which uses llm-prices.com)
        try {
            const pricing = await configService.getPricing(model);
            if (pricing.inputPer1M > 0 || pricing.outputPer1M > 0) {
                const inputCost = (prompt_tokens / 1_000_000) * pricing.inputPer1M;
                const outputCost = (completion_tokens / 1_000_000) * pricing.outputPer1M;
                totalCost = inputCost + outputCost;
            }
        }
        catch (err) {
            console.warn(`[CostTracker] Failed to fetch pricing for ${model}:`, err);
        }
        // Record the call
        const call = createLLMCall(provider, model, prompt_tokens, completion_tokens, totalCost);
        this.calls.push(call);
        if (!(provider in this.provider_stats)) {
            this.provider_stats[provider] = createProviderStats(provider);
        }
        const stats = this.provider_stats[provider];
        stats.calls += 1;
        stats.prompt_tokens += prompt_tokens;
        stats.completion_tokens += completion_tokens;
        stats.total_cost_usd += totalCost;
        return totalCost;
    }
}
//# sourceMappingURL=costTracker.js.map